/**
 * Métadonnées d'affichage pour les entrées d'audit eCable.
 *
 * Centralise : labels FR par action, icônes par domaine, couleurs par
 * sévérité, et utilitaires de formatage. Utilisé par toutes les vues
 * audit (liste, timeline, activité user, corrélation).
 */

import {
  Activity, AlertTriangle, Archive as ArchiveIcon, ArrowRightLeft, CheckCircle2,
  ClipboardList, Edit3, Eye, FileSignature, FileText, Globe2, KeyRound, LogIn,
  LogOut, Mail, ShieldAlert, ShieldCheck, ShieldX, Trash2, UserPlus, Users,
  XCircle, ListChecks, Send, Cog, Server, Download, Upload, FileWarning,
} from 'lucide-vue-next'

export type AuditSeverity = 'info' | 'warning' | 'critical'
export type AuditSource = 'backend' | 'frontend' | 'signal' | 'admin' | 'system'

export interface AuditLogEntry {
  id: number
  action: string
  created_at: string
  user: { id: number; username: string; full_name: string } | null
  courrier_id?: number | null
  target_type?: string | null
  target_id?: string | null
  details?: Record<string, any> | null
  before?: Record<string, any> | null
  after?: Record<string, any> | null
  ip_address?: string | null
  user_agent?: string
  session_key?: string
  request_id?: string | null
  severity: AuditSeverity
  source: AuditSource
}

// ---------------------------------------------------------------------------
// Labels FR (clé = constante backend dans audit_actions.py)
// ---------------------------------------------------------------------------

export const ACTION_LABELS: Record<string, string> = {
  // Auth
  'auth.login_success': 'Connexion réussie',
  'auth.login_failure': 'Échec de connexion',
  'auth.logout': 'Déconnexion',
  'auth.2fa_setup_started': '2FA — début configuration',
  'auth.2fa_setup_confirmed': '2FA — configurée',
  'auth.2fa_success': '2FA — code validé',
  'auth.2fa_failure': '2FA — code invalide',
  'auth.2fa_disabled': '2FA désactivée',
  'auth.password_reset_requested': 'Demande de réinitialisation MdP',
  'auth.password_reset_used': 'Mot de passe réinitialisé',
  'auth.password_changed': 'Mot de passe changé',
  'auth.account_activated': 'Compte activé',

  // Courriers
  'courrier.cree': 'Courrier créé',
  'courrier.consulte': 'Courrier consulté',
  'courrier.modifie': 'Courrier modifié',
  'courrier.valide': 'Courrier validé',
  'courrier.archive': 'Courrier archivé',
  'courrier.desarchive': 'Courrier désarchivé',
  'courrier.supprime': 'Courrier supprimé',
  'courrier.metadata_revisee': 'Métadonnées révisées (CSA)',
  'courrier.piece_jointe_ajoutee': 'PJ ajoutée',
  'courrier.piece_jointe_supprimee': 'PJ supprimée',
  'courrier.piece_jointe_telechargee': 'PJ téléchargée',

  // Affectations
  'affectation.creee': 'Affectation créée',
  'affectation.acceptee': 'Affectation acceptée',
  'affectation.rejetee': 'Affectation rejetée',
  'affectation.transferee': 'Affectation transférée',
  'affectation.reaffectee': 'Affectation réaffectée',
  'affectation.traitee': 'Affectation traitée',
  'affectation.supprimee': 'Affectation supprimée',
  'affectation.consultation_lancee': 'Consultation lancée',
  'affectation.consultation_retournee': 'Consultation retournée',

  // Notes
  'note.creee': 'Note créée',
  'note.consultee': 'Note consultée',
  'note.modifiee': 'Note modifiée',
  'note.soumise': 'Note soumise pour validation',
  'note.archivee': 'Note archivée',
  'note.telechargee': 'Note téléchargée',
  'note.supprimee': 'Note supprimée',
  'note.commentaire_ajoute': 'Commentaire ajouté',
  'note.piece_jointe_ajoutee': 'PJ note ajoutée',

  // Validations
  'validation.validee': 'Note validée',
  'validation.rejetee': 'Note rejetée',
  'validation.modifiee': 'Note modifiée par validateur',
  'validation.paraphe_appose': 'Paraphe apposé',

  // Signature
  'signature.demandee': 'Signature demandée',
  'signature.pki_initiee': 'Signature PKI initiée',
  'signature.completee': 'Signature terminée',
  'signature.echouee': 'Signature échouée',

  // Cables
  'cable.cree': 'Câble créé',
  'cable.formate': 'Câble formaté',
  'cable.envoye': 'Câble envoyé',

  // Admin
  'admin.user_cree': 'Utilisateur créé',
  'admin.user_modifie': 'Utilisateur modifié',
  'admin.user_desactive': 'Utilisateur désactivé',
  'admin.user_active': 'Utilisateur activé',
  'admin.user_supprime': 'Utilisateur supprimé',
  'admin.role_cree': 'Rôle créé',
  'admin.role_modifie': 'Rôle modifié',
  'admin.role_supprime': 'Rôle supprimé',
  'admin.permission_modifiee': 'Permission modifiée',
  'admin.direction_creee': 'Direction créée',
  'admin.direction_modifiee': 'Direction modifiée',
  'admin.direction_supprimee': 'Direction supprimée',
  'admin.departement_cree': 'Département créé',
  'admin.departement_modifie': 'Département modifié',
  'admin.departement_supprime': 'Département supprimé',
  'admin.service_cree': 'Service créé',
  'admin.service_modifie': 'Service modifié',
  'admin.service_supprime': 'Service supprimé',
  'admin.ambassade_creee': 'Ambassade créée',
  'admin.ambassade_modifiee': 'Ambassade modifiée',
  'admin.ambassade_supprimee': 'Ambassade supprimée',
  'admin.config_modifiee': 'Configuration modifiée',
  'securite.alerte_envoyee': 'Alerte de sécurité envoyée',
  'admin.delegation_activee': 'Suppléance activée',
  'admin.delegation_desactivee': 'Suppléance désactivée',

  // Système
  'system.extraction_job_demarre': 'Extraction démarrée',
  'system.extraction_job_termine': 'Extraction terminée',
  'system.extraction_job_echoue': 'Extraction échouée',
  'system.email_envoye': 'Email envoyé',
  'system.audit_export': 'Export audit (archive)',
}

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\./g, ' › ')
}

// ---------------------------------------------------------------------------
// Domaines (préfixe avant le 1er point) — icône + couleur
// ---------------------------------------------------------------------------

export interface DomainMeta {
  icon: any
  label: string
  // Tailwind classes (badge bg + text)
  badge: string
}

const DOMAIN_META: Record<string, DomainMeta> = {
  auth: { icon: KeyRound, label: 'Authentification', badge: 'bg-blue-50 text-blue-700 border border-blue-200' },
  courrier: { icon: Mail, label: 'Courrier', badge: 'bg-indigo-50 text-indigo-700 border border-indigo-200' },
  affectation: { icon: ArrowRightLeft, label: 'Affectation', badge: 'bg-cyan-50 text-cyan-700 border border-cyan-200' },
  note: { icon: FileText, label: 'Note', badge: 'bg-violet-50 text-violet-700 border border-violet-200' },
  validation: { icon: CheckCircle2, label: 'Validation', badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  signature: { icon: FileSignature, label: 'Signature', badge: 'bg-amber-50 text-amber-700 border border-amber-200' },
  cable: { icon: Send, label: 'Câble', badge: 'bg-pink-50 text-pink-700 border border-pink-200' },
  admin: { icon: Users, label: 'Administration', badge: 'bg-slate-100 text-slate-700 border border-slate-300' },
  system: { icon: Cog, label: 'Système', badge: 'bg-gray-100 text-gray-700 border border-gray-300' },
}

export function getDomain(action: string): string {
  return action.split('.')[0] || 'system'
}

export function getDomainMeta(action: string): DomainMeta {
  return DOMAIN_META[getDomain(action)] || DOMAIN_META.system
}

// ---------------------------------------------------------------------------
// Sévérité
// ---------------------------------------------------------------------------

export interface SeverityMeta {
  icon: any
  label: string
  badge: string  // ring + text
  ring: string   // bord gauche
  dot: string    // pastille
}

export function getSeverityMeta(severity?: AuditSeverity | string | null): SeverityMeta {
  return (severity && SEVERITY_META[severity as AuditSeverity]) || SEVERITY_META.info
}

export const SEVERITY_META: Record<AuditSeverity, SeverityMeta> = {
  info: {
    icon: Activity,
    label: 'Information',
    badge: 'bg-gray-100 text-gray-700',
    ring: 'border-l-gray-300',
    dot: 'bg-gray-400',
  },
  warning: {
    icon: AlertTriangle,
    label: 'Avertissement',
    badge: 'bg-amber-100 text-amber-800',
    ring: 'border-l-amber-400',
    dot: 'bg-amber-500',
  },
  critical: {
    icon: ShieldAlert,
    label: 'Critique',
    badge: 'bg-red-100 text-red-800',
    ring: 'border-l-red-500',
    dot: 'bg-red-600',
  },
}

// ---------------------------------------------------------------------------
// Source
// ---------------------------------------------------------------------------

export const SOURCE_META: Record<AuditSource, { icon: any; label: string; badge: string }> = {
  backend: { icon: Server, label: 'Backend API', badge: 'bg-slate-100 text-slate-700' },
  frontend: { icon: Globe2, label: 'Frontend (UI)', badge: 'bg-sky-100 text-sky-700' },
  signal: { icon: ShieldCheck, label: 'Signal Django', badge: 'bg-purple-100 text-purple-700' },
  admin: { icon: Users, label: 'Django Admin', badge: 'bg-orange-100 text-orange-700' },
  system: { icon: Cog, label: 'Système / Cron', badge: 'bg-gray-100 text-gray-700' },
}

export function getSourceMeta(source?: AuditSource | string | null) {
  return (source && SOURCE_META[source as AuditSource]) || SOURCE_META.system
}

// ---------------------------------------------------------------------------
// Cible (target_type)
// ---------------------------------------------------------------------------

const TARGET_LABELS: Record<string, string> = {
  courrier: 'Courrier',
  note: 'Note',
  affectation: 'Affectation',
  user: 'Utilisateur',
  role: 'Rôle',
  direction: 'Direction',
  cable: 'Câble',
  signaturetask: 'Tâche signature',
  notevalidation: 'Validation',
  noteattachment: 'PJ Note',
  piecejointe: 'PJ Courrier',
}

export function getTargetLabel(targetType?: string | null): string {
  if (!targetType) return ''
  return TARGET_LABELS[targetType.toLowerCase()] || targetType
}

// ---------------------------------------------------------------------------
// Formatage date
// ---------------------------------------------------------------------------

export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

export function formatDateShort(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function formatRelative(iso: string): string {
  const d = new Date(iso).getTime()
  const now = Date.now()
  const diff = Math.floor((now - d) / 1000)
  if (diff < 60) return 'à l\'instant'
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`
  return formatDateShort(iso)
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MiB`
}

// ---------------------------------------------------------------------------
// Listes pour les filtres (groupés par domaine)
// ---------------------------------------------------------------------------

export const ACTION_GROUPS: Array<{ domain: string; label: string; actions: string[] }> = [
  { domain: 'auth', label: 'Authentification', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('auth.')) },
  { domain: 'courrier', label: 'Courriers', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('courrier.')) },
  { domain: 'affectation', label: 'Affectations', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('affectation.')) },
  { domain: 'note', label: 'Notes', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('note.')) },
  { domain: 'validation', label: 'Validations', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('validation.')) },
  { domain: 'signature', label: 'Signatures', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('signature.')) },
  { domain: 'cable', label: 'Câbles', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('cable.')) },
  { domain: 'admin', label: 'Administration', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('admin.')) },
  { domain: 'system', label: 'Système', actions: Object.keys(ACTION_LABELS).filter(a => a.startsWith('system.')) },
]
